import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { V4_T, inferPropertyEditDefaults, getBillingGroupOverlapConflicts } from './propertyDetailHelpers'
import BillingGroupMemberPicker from './BillingGroupMemberPicker'
import BillingGroupConflictNotice from './BillingGroupConflictNotice'

// Frequency options shared by the maintenance / irrigation / herbicide selects.
// Value 'none' represents "not serviced" so the field is never left ambiguous.
const PROPERTY_FREQUENCY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-Annually' },
  { value: 'annual', label: 'Annually' },
  { value: 'asneeded', label: 'As Needed' }
]

const getDefaultCrewSize = (property = {}) => {
  const size = Number(property.assignedRouteCrewSize || property.prescribedCrewSize || property.internalEstimateCrewCount || property.crewSize || 3)
  return Number.isFinite(size) && size > 0 ? size : 3
}

const formatHours = (value) => {
  const hours = Number(value || 0)
  if (!hours) return '0h'
  const roundedMinutes = Math.round(hours * 60)
  const wholeHours = Math.floor(roundedMinutes / 60)
  const minutes = roundedMinutes % 60
  if (!wholeHours) return `${minutes}m`
  if (!minutes) return `${wholeHours}h`
  return `${wholeHours}h ${minutes}m`
}

const formatDecimal = (value) => {
  const num = Number(value || 0)
  if (!num) return ''
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

const selectOnFocus = (event) => {
  event.target.select()
}

const getBillingGroupPropertyId = (property = {}) => {
  const id = property.aspirePropertyId || property.propertyId || property.PropertyID
  const numeric = Number(id)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

// Combine the picker's member ids with the auto-included current property,
// then dedupe and sort. The picker already excludes the current property and
// non-numeric values, so this is mainly the "pin current property" step.
const buildBillingGroupMemberIds = (memberIds, currentPropertyId) => {
  const ids = (Array.isArray(memberIds) ? memberIds : [])
    .map(id => Number(id))
    .filter(id => Number.isFinite(id) && id > 0)
  if (currentPropertyId) ids.push(currentPropertyId)
  return [...new Set(ids)].sort((a, b) => a - b)
}

// Edit-property modal. UI-only for now — captures fields into local state and
// reports them via onSave; persistence is wired up in a later pass.
function PropertyEditModal ({ open, onClose, property, onSave, crews, propertyOptions = [] }) {
  const [prescribedMode, setPrescribedMode] = useState('man')
  const [prescribedManHours, setPrescribedManHours] = useState('')
  const [prescribedClockHours, setPrescribedClockHours] = useState('')
  const [prescribedCrewSize, setPrescribedCrewSize] = useState('3')
  const [mtcFrequency, setMtcFrequency] = useState('none')
  const [irrigationFrequency, setIrrigationFrequency] = useState('none')
  const [herbicideFrequency, setHerbicideFrequency] = useState('none')
  const [mowingRequired, setMowingRequired] = useState(false)
  const [geofenceSet, setGeofenceSet] = useState(false)
  const [propertyLinesVerified, setPropertyLinesVerified] = useState(false)
  const [takeoffFinished, setTakeoffFinished] = useState(false)
  const [takeoffNotes, setTakeoffNotes] = useState('')
  const [lockToCrew, setLockToCrew] = useState(null)
  const [irrigationTech, setIrrigationTech] = useState(null)
  const [noIrrigation, setNoIrrigation] = useState(false)
  const [billingGroupId, setBillingGroupId] = useState(null)
  const [billingGroupName, setBillingGroupName] = useState('')
  const [billingGroupMemberIds, setBillingGroupMemberIds] = useState([])
  const [billingGroupNotes, setBillingGroupNotes] = useState('')
  const [billingGroupError, setBillingGroupError] = useState('')
  const [billingGroupConflicts, setBillingGroupConflicts] = useState(null)
  const [saving, setSaving] = useState(false)

  // Seed the form from the property each time the modal opens.
  useEffect(() => {
    if (!open) return
    const p = property || {}
    const crewList = crews || []
    const inferred = inferPropertyEditDefaults(p)
    const crewSize = getDefaultCrewSize(p)
    const manHours = p.prescribedManHours != null ? Number(p.prescribedManHours) : 0
    setPrescribedMode('clock')
    setPrescribedCrewSize(formatDecimal(crewSize))
    setPrescribedManHours(manHours ? formatDecimal(manHours) : '')
    setPrescribedClockHours(manHours ? formatDecimal(manHours / crewSize) : '')
    setMtcFrequency(inferred.mtcFrequency)
    setIrrigationFrequency(inferred.irrigationCheckFrequency)
    setHerbicideFrequency(inferred.herbicideApplicationFrequency)
    setMowingRequired(!!p.mowingEnabled)
    // Human-confirmed setup checklist (geofence / property lines / takeoff). Each
    // rides on p.setupChecklist.<item>.confirmed; takeoff notes come off the item.
    const cl = p.setupChecklist || {}
    setGeofenceSet(!!cl.geofenceSet?.confirmed)
    setPropertyLinesVerified(!!cl.propertyLinesVerified?.confirmed)
    setTakeoffFinished(!!cl.takeoffComplete?.confirmed)
    setTakeoffNotes(cl.takeoffComplete?.notes || '')
    setLockToCrew(p.lockToCrewId ? (crewList.find(c => c._id === p.lockToCrewId) || null) : null)
    setIrrigationTech(p.irrigationTechId ? (crewList.find(c => c._id === p.irrigationTechId) || null) : null)
    setNoIrrigation(inferred.noIrrigation)
    setBillingGroupId(p.billingGroup?._id || null)
    setBillingGroupName(p.billingGroup?.name || '')
    const currentId = getBillingGroupPropertyId(p)
    setBillingGroupMemberIds(
      (Array.isArray(p.billingGroup?.memberAspirePropertyIds) ? p.billingGroup.memberAspirePropertyIds : [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0 && id !== currentId)
    )
    setBillingGroupNotes(p.billingGroup?.notes || '')
    setBillingGroupError('')
    setBillingGroupConflicts(null)
    setSaving(false)
  }, [open, property])

  const handleSave = async (force = false) => {
    const crewSize = Number(prescribedCrewSize || 0)
    const clockHours = Number(prescribedClockHours || 0)
    const manHours = prescribedMode === 'clock'
      ? (clockHours > 0 && crewSize > 0 ? clockHours * crewSize : null)
      : (prescribedManHours === '' ? null : Number(prescribedManHours))
    const billingGroupMembers = buildBillingGroupMemberIds(billingGroupMemberIds, getBillingGroupPropertyId(property || {}))
    const hasName = !!billingGroupName.trim()
    const hasMembers = (Array.isArray(billingGroupMemberIds) ? billingGroupMemberIds.length : 0) > 0
    // Treat members-without-a-name as an in-progress group rather than silently
    // dropping it on save.
    if (hasMembers && !hasName) {
      setBillingGroupError('Give the billing group a name to save it.')
      return
    }
    const shouldSaveBillingGroup = hasName
    if (shouldSaveBillingGroup && billingGroupMembers.length < 2) {
      setBillingGroupError('Add at least one other property to the group.')
      return
    }
    const payload = {
      prescribedManHours: manHours == null ? null : Math.round(manHours * 100) / 100,
      mtcFrequency,
      irrigationCheckFrequency: irrigationFrequency,
      herbicideApplicationFrequency: herbicideFrequency,
      mowingEnabled: mowingRequired,
      setupChecklist: {
        geofenceSet: { confirmed: geofenceSet },
        propertyLinesVerified: { confirmed: propertyLinesVerified },
        takeoffComplete: { confirmed: takeoffFinished, notes: takeoffNotes.trim() || null }
      },
      lockToCrewId: lockToCrew?._id || null,
      irrigationTechId: noIrrigation ? null : (irrigationTech?._id || null),
      noIrrigation,
      billingGroup: shouldSaveBillingGroup
        ? {
            _id: billingGroupId || null,
            name: billingGroupName.trim(),
            memberAspirePropertyIds: billingGroupMembers,
            notes: billingGroupNotes.trim(),
            active: true
          }
        : null
    }
    setSaving(true)
    setBillingGroupError('')
    try {
      // Await so server-side billing errors (e.g. overlap) surface in the modal
      // instead of being swallowed after an early close.
      if (onSave) await onSave(payload, { force })
      onClose()
    } catch (err) {
      const conflicts = getBillingGroupOverlapConflicts(err)
      if (conflicts) {
        setBillingGroupConflicts(conflicts)
        setBillingGroupError('')
      } else {
        setBillingGroupConflicts(null)
        setBillingGroupError(err?.response?.data?.message || err?.message || 'Failed to save billing group.')
      }
    } finally {
      setSaving(false)
    }
  }

  const labelStyle = { fontSize: 12, fontWeight: 600, color: V4_T.ink2, marginBottom: 4, display: 'block' }
  const fieldWrap = { marginBottom: 16 }
  const currentCrewSize = Number(prescribedCrewSize || 0) || getDefaultCrewSize(property || {})
  const currentManHours = prescribedMode === 'clock'
    ? (Number(prescribedClockHours || 0) * currentCrewSize)
    : Number(prescribedManHours || 0)
  const currentClockHours = currentCrewSize > 0 ? currentManHours / currentCrewSize : 0

  const renderFrequency = (label, value, setValue) => (
    <div style={fieldWrap}>
      <span style={labelStyle}>{label}</span>
      <FormControl fullWidth size="small" variant="outlined">
        <Select value={value} onChange={(e) => setValue(e.target.value)}>
          {PROPERTY_FREQUENCY_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )

  const getCrewOptionLabel = (c) => c?.name || c?.foremanName || ''
  const getCrewOptionSelected = (o, v) => o?._id === v?._id

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ borderBottom: `1px solid ${V4_T.line}`, paddingBottom: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: V4_T.ink }}>Edit Property</span>
          <span style={{ fontSize: 12, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 2 }}>
            {property?.name || property?.propertyName || 'Property'}
          </span>
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        <div style={fieldWrap}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
            <span style={{ ...labelStyle, marginBottom: 0 }}>Prescribed Time</span>
            <div style={{ display: 'flex', border: `1px solid ${V4_T.line}`, borderRadius: 6, overflow: 'hidden' }}>
              {[
                ['man', 'Man-hours'],
                ['clock', 'Clock time']
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPrescribedMode(value)}
                  style={{
                    border: 'none',
                    borderRight: value === 'man' ? `1px solid ${V4_T.line}` : 'none',
                    background: prescribedMode === value ? V4_T.green : '#fff',
                    color: prescribedMode === value ? '#fff' : V4_T.ink2,
                    padding: '5px 9px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {prescribedMode === 'man' ? (
            <TextField
              type="number"
              fullWidth
              size="small"
              variant="outlined"
              value={prescribedManHours}
              onFocus={selectOnFocus}
              onChange={(e) => {
                const next = e.target.value
                setPrescribedManHours(next)
                const crewSize = Number(prescribedCrewSize || 0)
                setPrescribedClockHours(next !== '' && crewSize > 0 ? formatDecimal(Number(next) / crewSize) : '')
              }}
              placeholder="e.g. 9"
              inputProps={{ min: 0, step: 0.25 }}
            />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="outlined"
                label="Clock hours"
                value={prescribedClockHours}
                onFocus={selectOnFocus}
                onChange={(e) => {
                  const next = e.target.value
                  setPrescribedClockHours(next)
                  const crewSize = Number(prescribedCrewSize || 0)
                  setPrescribedManHours(next !== '' && crewSize > 0 ? formatDecimal(Number(next) * crewSize) : '')
                }}
                placeholder="e.g. 2.25"
                inputProps={{ min: 0, step: 0.25 }}
              />
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="outlined"
                label="Crew"
                value={prescribedCrewSize}
                onFocus={selectOnFocus}
                onChange={(e) => {
                  const next = e.target.value
                  setPrescribedCrewSize(next)
                  const crewSize = Number(next || 0)
                  const clockHours = Number(prescribedClockHours || 0)
                  setPrescribedManHours(clockHours > 0 && crewSize > 0 ? formatDecimal(clockHours * crewSize) : prescribedManHours)
                }}
                inputProps={{ min: 1, step: 1 }}
              />
            </div>
          )}
          <div style={{ marginTop: 7, fontSize: 11, color: V4_T.muteSoft, lineHeight: 1.4 }}>
            Saves as <strong>{formatDecimal(currentManHours) || '0'} man-hours</strong>.
            {currentCrewSize > 0 && currentManHours > 0
              ? ` At ${formatDecimal(currentCrewSize)} crew, that is about ${formatHours(currentClockHours)} clock time.`
              : ' Enter either man-hours or clock time with crew size.'}
          </div>
        </div>

        {renderFrequency('Maintenance Frequency', mtcFrequency, setMtcFrequency)}
        {renderFrequency('Irrigation Check Frequency', irrigationFrequency, setIrrigationFrequency)}
        {renderFrequency('Herbicide Application Frequency', herbicideFrequency, setHerbicideFrequency)}

        <div style={fieldWrap}>
          <span style={labelStyle}>Lock to Crew</span>
          <Autocomplete
            options={crews || []}
            getOptionLabel={getCrewOptionLabel}
            getOptionSelected={getCrewOptionSelected}
            value={lockToCrew}
            onChange={(_, val) => setLockToCrew(val)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" placeholder="Any crew" />
            )}
          />
        </div>

        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={noIrrigation}
                onChange={(e) => {
                  setNoIrrigation(e.target.checked)
                  if (e.target.checked) setIrrigationTech(null)
                }}
                color="primary"
              />
            }
            label={
              <span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>
                {noIrrigation ? 'Property Has No Irrigation' : 'Property Has Irrigation (Requires Inspections)'}
              </span>
            }
          />
        </div>

        {!noIrrigation && (
          <div style={fieldWrap}>
            <span style={labelStyle}>Assigned Irrigation Tech</span>
            <Autocomplete
              options={crews || []}
              getOptionLabel={getCrewOptionLabel}
              getOptionSelected={getCrewOptionSelected}
              value={irrigationTech}
              onChange={(_, val) => setIrrigationTech(val)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="No tech assigned" />
              )}
            />
          </div>
        )}

        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <FormControlLabel
            control={<Switch checked={mowingRequired} onChange={(e) => setMowingRequired(e.target.checked)} color="primary" />}
            label={<span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>Mowing Required</span>}
          />
        </div>

        <div style={{ ...fieldWrap, marginBottom: 4 }}>
          <span style={labelStyle}>Setup Checklist</span>
          <ConfirmRow
            label="Geofence set"
            checked={geofenceSet}
            onChange={setGeofenceSet}
            stamp={property?.setupChecklist?.geofenceSet}
          />
          <ConfirmRow
            label="Property lines verified"
            checked={propertyLinesVerified}
            onChange={setPropertyLinesVerified}
            stamp={property?.setupChecklist?.propertyLinesVerified}
          />
          <ConfirmRow
            label="Takeoff complete"
            checked={takeoffFinished}
            onChange={setTakeoffFinished}
            stamp={property?.setupChecklist?.takeoffComplete}
          />
          {takeoffFinished && (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={takeoffNotes}
              onChange={(e) => setTakeoffNotes(e.target.value)}
              placeholder="Takeoff notes (optional)"
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <div style={{ borderTop: `1px solid ${V4_T.line}`, marginTop: 16, paddingTop: 16 }}>
          <span style={labelStyle}>Billing Group</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={billingGroupName}
              onChange={(e) => {
                setBillingGroupName(e.target.value)
                setBillingGroupError('')
              }}
              placeholder="Group name"
            />
            <BillingGroupMemberPicker
              value={billingGroupMemberIds}
              onChange={(ids) => {
                setBillingGroupMemberIds(ids)
                setBillingGroupError('')
                setBillingGroupConflicts(null)
              }}
              currentPropertyId={getBillingGroupPropertyId(property || {})}
              propertyOptions={propertyOptions}
              error={!!billingGroupError}
              helperText={billingGroupError || undefined}
            />
            <BillingGroupConflictNotice
              conflicts={billingGroupConflicts}
              saving={saving}
              onConfirm={() => handleSave(true)}
              onCancel={() => setBillingGroupConflicts(null)}
            />
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={billingGroupNotes}
              onChange={(e) => setBillingGroupNotes(e.target.value)}
              placeholder="Notes"
              multiline
              minRows={2}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid ${V4_T.line}`, padding: '12px 24px' }}>
        <Button onClick={onClose} disabled={saving} style={{ color: V4_T.ink2 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => handleSave(false)}
          disabled={saving}
          style={{ fontWeight: 600, background: V4_T.green, color: '#fff' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// One setup-checklist confirm row: a switch + the audit stamp (who/when) when
// the item has been confirmed. `stamp` is the { confirmed, confirmedAt, confirmedBy }
// subdoc from the property's setupChecklist.
function ConfirmRow ({ label, checked, onChange, stamp }) {
  return (
    <div style={{ marginBottom: 2 }}>
      <FormControlLabel
        control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} color="primary" />}
        label={<span style={{ fontSize: 13, fontWeight: 600, color: V4_T.ink2 }}>{label}</span>}
      />
      {stamp?.confirmedAt && (
        <div style={{ fontSize: 11, color: V4_T.mute, marginLeft: 4, marginTop: -2 }}>
          Confirmed {new Date(stamp.confirmedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          {stamp.confirmedBy ? ` · ${stamp.confirmedBy}` : ''}
        </div>
      )}
    </div>
  )
}

export default PropertyEditModal
